import { Loader } from "@bfc-software/pallet";
import type { TFunction } from "i18next";

import { usePlanTasks } from "../../-data/use-plans";
import { TaskTable } from "../../-components/task-table";
import sharedStyles from "../../-components/shared.module.css";
import type { TaskFilters } from "../../-data/filters";
import type { Task, TaskPlan } from "../../-data/types";

import { planTasksStateFrom } from "./-state";

/**
 * One open plan card's tasks.
 *
 * Its own component because its own hook: `usePlanTasks` is per plan, and hooks
 * cannot be called conditionally - so the only way for the fetch to be the
 * expansion is for the expansion to be a mount. The card renders this while
 * open and nothing while closed, and react-query's cache is what makes the
 * second open free.
 *
 * Three states, drawn as three states. A plan whose tasks failed to load is not
 * a plan with no tasks, and the card above it is still perfectly accurate - so
 * the failure stays inside the expansion rather than taking out the tab.
 */
export function PlanTasks({
  orgId,
  warehouseId,
  plan,
  filters,
  onSelectTask,
  t,
}: {
  orgId: string | null;
  warehouseId: string;
  /** The whole plan, not just its id: the card's name comes off it too. */
  plan: TaskPlan;
  filters: TaskFilters;
  /** Passed straight to `TaskTable`, which hands back the whole clicked row. */
  onSelectTask: (task: Task) => void;
  t: TFunction;
}) {
  const state = planTasksStateFrom(
    usePlanTasks({ orgId, warehouseId, plan, filters }),
  );

  switch (state.kind) {
    case "loading":
      return (
        <div data-test-role="plan-tasks-loading">
          <Loader type="inline" />
        </div>
      );
    case "failed":
      return (
        <p className={sharedStyles.muted} data-test-role="plan-tasks-failed">
          {t("tasks.plans.tasksFailed")}
        </p>
      );
    case "loaded":
      break;
  }

  const { items } = state.value;

  if (items.length === 0) {
    /*
      One answer rather than two, now that the filters are part of the request:
      the rows this card was handed are what the plan holds under the filter bar,
      so there is no longer a difference between a plan holding nothing and a
      plan whose rows this side then hid.

      This is also what an unactivated plan whose own *planning* produced no
      tasks draws (see §0 of the implementation plan: TaskPlanSummary.status is
      a different field from activation.status, and a plan can fail to generate
      any task rows at all). Auditing why planning failed is out of scope for
      this screen - the empty state is the same one a filtered-to-nothing card
      already draws, and that is deliberate rather than a gap.
    */
    return (
      <p className={sharedStyles.muted} data-test-role="plan-tasks-empty">
        {t("tasks.all.empty")}
      </p>
    );
  }

  return (
    <TaskTable
      rows={items}
      ariaLabel={t("tasks.plans.tasksAriaLabel", { plan: plan.name })}
      onSelect={onSelectTask}
      t={t}
    />
  );
}
